import { Link, useParams } from 'react-router-dom'
import { Faq } from '../components/Faq'
import { carrierFaq } from '../data/carrier-faq'
import {
  carrierPageDescription,
  carrierPageHeading,
  carrierPageTitle,
  carrierScheduleAnswer,
} from '../data/carrier-seo.mjs'
import rawStats from '../data/carrier-stats.generated.json'
import { CARRIERS, carrierBySlug } from '../data/carriers'
import { reliabilityFacts, reliabilitySentence } from '../lib/carrier-reliability'
import { usePageMeta } from '../lib/usePageMeta'

// Visible "data as of" freshness stamp, injected at build (matches prerender.carriers.mjs).
const BUILD_DATE = typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : ''

// Per-carrier schedule stats keyed by carrier slug (16 of 17 carriers; SITC has no entry). ~30KB, safe
// to import into the bundle. Every number is derived from tracked sailings, not invented.
interface CarrierTopLane {
  slug: string
  o_name: string
  d_name: string
  sailings: number
  median_transit: number
}
interface CarrierVolatility {
  active_sailings: number
  revisions_per_sailing: number
  mean_abs_etd_hours: number
  p90_abs_etd_hours: number
  late_etd_share: number
  window_days: number
}
interface CarrierRelLane {
  slug: string
  o_name: string
  d_name: string
  on_time_pct: number
  observations: number
}
interface CarrierArrivalReliability {
  on_time_pct: number
  observations: number
  lanes_rated: number
  best_lanes: CarrierRelLane[]
  worst_lanes: CarrierRelLane[]
}
interface CarrierStats {
  scac: string
  sailings: number
  lanes: number
  services: number
  top_lanes: CarrierTopLane[]
  volatility: CarrierVolatility | null
  arrival_reliability?: CarrierArrivalReliability | null
}
const CARRIER_STATS = rawStats as Record<string, CarrierStats | undefined>

function StatCard({ value, label }: { value: string; label: string }) {
  return (
    <div className="bg-surface-0 border border-border rounded-xl px-4 py-4">
      <div className="text-[var(--text-primary)] text-2xl font-bold font-mono leading-tight">
        {value}
      </div>
      <div className="text-[var(--text-muted)] text-xs uppercase tracking-wide mt-1.5">{label}</div>
    </div>
  )
}

const CTH = 'text-left p-3 text-xs uppercase tracking-wide text-[var(--text-muted)]'

function sourcingLine(c: { name: string; source: string }) {
  if (c.source === 'connector')
    return `${c.name} sailings are pulled directly from ${c.name}'s own e-service, then normalised into the same schedule shape as every other line.`
  if (c.source === 'official_api')
    return `${c.name} sailings come from ${c.name}'s published schedule API, normalised into the same schedule shape as every other line.`
  return `${c.name} forward sailings are sourced from ${c.name}'s published schedule feed and normalised into the same schedule shape as every other line.`
}

export function CarrierPage() {
  const { slug: rawSlug } = useParams<{ slug: string }>()
  const slug = rawSlug?.replace(/\.html$/i, '').replace(/\/+$/, '')
  const c = slug ? carrierBySlug(slug) : undefined
  const stats = c ? CARRIER_STATS[c.slug] : undefined

  // Meta is set unconditionally (hooks can't be conditional). For an unknown slug we fall back to the
  // hub meta; the static build only emits pages for real slugs, and the edge middleware 404s unknown
  // ones, so a human only reaches this branch by client-navigating to a bad slug.
  usePageMeta(
    c ? carrierPageTitle(c, new Date().getUTCFullYear()) : 'Carrier not found · SchedulesMCP',
    c
      ? carrierPageDescription(c, stats)
      : 'That carrier page does not exist. Browse the ocean carriers SchedulesMCP covers.'
  )

  if (!c) {
    return (
      <main className="max-w-3xl mx-auto px-6 pt-12 pb-16">
        <h1
          className="font-display text-3xl mb-3"
          style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
        >
          Carrier not found
        </h1>
        <p className="text-[var(--text-secondary)] mb-6">
          We don't have a schedule page for that carrier.
        </p>
        <Link to="/carriers" className="text-signal no-underline hover:underline">
          See all carriers →
        </Link>
      </main>
    )
  }

  const related = CARRIERS.filter((x) => x.slug !== c.slug).slice(0, 8)

  return (
    <main className="max-w-3xl mx-auto px-6 pt-12 pb-16">
      <nav className="text-sm text-[var(--text-muted)] mb-5">
        <Link to="/" className="no-underline hover:underline">
          SchedulesMCP
        </Link>{' '}
        ›{' '}
        <Link to="/carriers" className="no-underline hover:underline">
          Carriers
        </Link>{' '}
        › {c.name}
      </nav>

      <p
        className="m-0 font-mono text-xs uppercase text-accent"
        style={{ letterSpacing: '0.14em' }}
      >
        {c.scac} · {c.hq}
      </p>
      <h1
        className="font-display text-4xl mt-3 mb-4"
        style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
      >
        {carrierPageHeading(c)}
      </h1>
      {carrierScheduleAnswer(c, stats) && (
        <p className="text-[var(--text-primary)] text-lg font-medium mb-3 max-w-2xl leading-relaxed">
          {carrierScheduleAnswer(c, stats)}
        </p>
      )}
      <p className="text-[var(--text-primary)] text-lg font-medium mb-2 max-w-2xl leading-relaxed">
        {(() => {
          const f = reliabilityFacts(stats)
          return f
            ? `${reliabilitySentence(c.name, f)} For your exact route, compare ${c.name} against every other line on the lane by transit time and observed arrival reliability before you book.`
            : `${c.name} (${c.full}) forward sailings are tracked and normalised into one comparable shape. On-time reliability is scored per lane from actual-versus-scheduled arrivals. Compare ${c.name} on your exact origin and destination, where thin lanes are marked low-sample rather than guessed.`
        })()}
      </p>
      <p className="text-[var(--text-muted)] text-xs mb-6">
        {c.scac} · schedule data as of {BUILD_DATE}.
      </p>

      <div className="bg-surface-0 border border-border rounded-xl p-5 text-[var(--text-secondary)] leading-relaxed">
        {sourcingLine(c)} {c.about}
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <Link
          to="/compare"
          className="bg-signal text-on-accent font-bold px-6 py-3 rounded-lg no-underline hover:opacity-90"
        >
          Compare {c.name} on your lane
        </Link>
        <Link
          to="/carriers"
          className="border border-border px-6 py-3 rounded-lg no-underline hover:border-signal"
        >
          All carriers
        </Link>
      </div>

      <h2 className="font-display font-light text-xl tracking-[-0.015em] mt-10 mb-3">
        What you get for {c.name}
      </h2>
      <ul className="space-y-2 text-[var(--text-secondary)] leading-relaxed list-disc pl-5">
        <li>
          <strong className="text-[var(--text-primary)]">Forward sailings</strong>: vessel, voyage,
          service loop, departure and arrival dates, and cut-offs where {c.name} publishes them.
        </li>
        <li>
          <strong className="text-[var(--text-primary)]">Coverage</strong>: {c.coverage}.
        </li>
        <li>
          <strong className="text-[var(--text-primary)]">Reliability</strong>: once a lane has
          enough tracked port calls, we score {c.name} on actual-vs-scheduled arrival, not on the
          schedule it published. Thin lanes are shown as low-sample, never guessed.
        </li>
        <li>
          <strong className="text-[var(--text-primary)]">Same shape as every line</strong>: {c.name}{' '}
          reads the same as MSC, Maersk, or any other carrier, so you can rank them side by side.
        </li>
      </ul>

      <h2 className="font-display font-light text-xl tracking-[-0.015em] mt-10 mb-3">
        How {c.name} reliability is scored
      </h2>
      <p className="text-[var(--text-secondary)] leading-relaxed max-w-2xl">
        We don't take a carrier's word for its transit time. When SchedulesMCP has enough tracked
        arrivals on a {c.name} lane, it compares each actual arrival against what was scheduled and
        reports the observed on-time rate and the median and p90 transit. Lanes without enough
        observations are labelled low-sample rather than given a made-up score. A number, when you
        see one, is real.
      </p>

      {stats && (
        <>
          <h2 className="font-display font-light text-xl tracking-[-0.015em] mt-10 mb-3">
            Schedule footprint
          </h2>
          <p className="text-[var(--text-secondary)] leading-relaxed max-w-2xl mb-4">
            What we currently track for {c.name} across the forward schedule window: the sailings,
            the distinct port-pairs, and the named service loops behind them.
          </p>
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))' }}
          >
            <StatCard value={stats.sailings.toLocaleString('en-US')} label="sailings tracked" />
            <StatCard value={stats.lanes.toLocaleString('en-US')} label="lanes served" />
            <StatCard value={stats.services.toLocaleString('en-US')} label="named services" />
          </div>

          {stats.top_lanes.length > 0 && (
            <>
              <h2 className="font-display font-light text-xl tracking-[-0.015em] mt-10 mb-3">
                Top lanes {c.name} sails
              </h2>
              <p className="text-[var(--text-secondary)] leading-relaxed max-w-2xl mb-4">
                The busiest port-pairs on {c.name}'s network by tracked sailings. Open a lane for
                the full carrier line-up and upcoming departures.
              </p>
              <div className="overflow-x-auto border border-border rounded-xl">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className={CTH}>Lane</th>
                      <th className={CTH}>Sailings</th>
                      <th className={CTH}>Median transit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.top_lanes.map((l) => (
                      <tr key={l.slug} className="border-b border-border">
                        <td className="p-3">
                          <Link
                            to={`/lanes/${l.slug}`}
                            className="text-signal font-semibold no-underline hover:underline"
                          >
                            {l.o_name} → {l.d_name}
                          </Link>
                        </td>
                        <td className="p-3 text-[var(--text-muted)] font-mono">{l.sailings}</td>
                        <td className="p-3 text-[var(--text-muted)] font-mono">
                          {l.median_transit != null ? `${l.median_transit} days` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {stats.volatility && (
            <>
              <h2 className="font-display font-light text-xl tracking-[-0.015em] mt-10 mb-3">
                Schedule reliability
              </h2>
              <p className="text-[var(--text-secondary)] leading-relaxed max-w-2xl mb-4">
                How much {c.name}'s published departures moved over the last{' '}
                {stats.volatility.window_days} days, measured from the revisions to their own ETDs,
                not from a survey. Higher drift means the board shifts more often after it's posted.
              </p>
              <div
                className="grid gap-3"
                style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))' }}
              >
                <StatCard
                  value={String(stats.volatility.revisions_per_sailing)}
                  label="schedule revisions per sailing"
                />
                <StatCard
                  value={`${Math.round(stats.volatility.late_etd_share * 1000) / 10}%`}
                  label="share departing behind schedule"
                />
                <StatCard
                  value={`${stats.volatility.p90_abs_etd_hours}h`}
                  label="90th-percentile ETD drift, hours"
                />
              </div>
            </>
          )}

          {stats.arrival_reliability &&
            (() => {
              const ar = stats.arrival_reliability as CarrierArrivalReliability
              const best = ar.best_lanes[0]
              const worst = ar.worst_lanes[0]
              const relTable = (lanes: CarrierRelLane[]) => (
                <div className="overflow-x-auto border border-border rounded-xl mb-4">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className={CTH}>Lane</th>
                        <th className={CTH}>On-time arrivals</th>
                        <th className={CTH}>Tracked sailings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lanes.map((l) => (
                        <tr key={l.slug} className="border-b border-border">
                          <td className="p-3">
                            <Link
                              to={`/lanes/${l.slug}`}
                              className="text-signal font-semibold no-underline hover:underline"
                            >
                              {l.o_name} → {l.d_name}
                            </Link>
                          </td>
                          <td className="p-3 text-[var(--text-primary)] font-mono font-semibold">
                            {Math.round(l.on_time_pct)}%
                          </td>
                          <td className="p-3 text-[var(--text-muted)] font-mono">
                            {l.observations}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
              return (
                <>
                  <h2 className="font-display font-light text-xl tracking-[-0.015em] mt-10 mb-3">
                    How reliable is {c.name} for on-time arrival?
                  </h2>
                  <p className="text-[var(--text-primary)] leading-relaxed max-w-2xl mb-4">
                    Across {ar.lanes_rated} lanes and {ar.observations.toLocaleString('en-US')}{' '}
                    tracked sailings, {c.name} ships arrive on schedule about{' '}
                    <strong>{Math.round(ar.on_time_pct)}%</strong> of the time, measured from actual
                    versus forecast arrivals, not the published timetable.
                    {best
                      ? ` It is most reliable on ${best.o_name} → ${best.d_name} (${Math.round(best.on_time_pct)}%)`
                      : ''}
                    {worst
                      ? `, and least reliable on ${worst.o_name} → ${worst.d_name} (${Math.round(worst.on_time_pct)}%).`
                      : best
                        ? '.'
                        : ''}
                  </p>
                  <h3 className="font-display font-semibold text-base mt-6 mb-2">
                    Most reliable lanes
                  </h3>
                  {relTable(ar.best_lanes)}
                  <h3 className="font-display font-semibold text-base mt-6 mb-2">
                    Least reliable lanes
                  </h3>
                  {relTable(ar.worst_lanes)}
                </>
              )
            })()}
        </>
      )}

      <Faq
        id={`carrier-${c.slug}`}
        items={(() => {
          const f = reliabilityFacts(stats)
          const ar = stats?.arrival_reliability
          const base = carrierFaq(c)
          const extra = []
          if (ar)
            extra.push({
              q: `How reliable is ${c.name} for on-time arrival?`,
              a: `Across ${ar.lanes_rated} lanes and ${ar.observations.toLocaleString('en-US')} tracked sailings, ${c.name} ships arrive on schedule about ${Math.round(ar.on_time_pct)}% of the time (actual vs forecast arrivals)${ar.best_lanes[0] ? `, best on ${ar.best_lanes[0].o_name} → ${ar.best_lanes[0].d_name} at ${Math.round(ar.best_lanes[0].on_time_pct)}%` : ''}.`,
            })
          if (f) extra.push({ q: `How reliable is ${c.name}?`, a: reliabilitySentence(c.name, f) })
          return [...extra, ...base]
        })()}
        heading={`${c.name} schedules & reliability: FAQ`}
      />

      <h2 className="font-display font-light text-xl tracking-[-0.015em] mt-10 mb-4">
        Other carriers
      </h2>
      <div className="flex flex-wrap gap-2">
        {related.map((r) => (
          <Link
            key={r.slug}
            to={`/carriers/${r.slug}`}
            className="bg-surface-0 border border-border rounded-full px-4 py-2 text-sm text-[var(--text-secondary)] no-underline hover:border-signal"
          >
            {r.name}
          </Link>
        ))}
        <Link
          to="/carriers"
          className="rounded-full px-4 py-2 text-sm text-signal no-underline hover:underline border border-[color:rgb(var(--accent-rgb)/0.3)]"
        >
          All {CARRIERS.length} carriers →
        </Link>
      </div>
    </main>
  )
}

export default CarrierPage
